<template>
  <div class="flex flex-col gap-2" data-testid="activity-requests-filter-bar">
    <div class="flex flex-wrap items-center gap-2">
      <label class="relative flex items-center">
        <span class="sr-only">Buscar por médico, CRM ou #id</span>
        <input
          ref="searchInput"
          type="search"
          :value="searchText"
          placeholder="Médico, CRM ou #id"
          class="h-8 w-64 rounded-md border border-lightGray30 px-3 pr-7 text-sm"
          data-testid="activity-requests-search-input"
          @input="onSearchInput(($event.target as HTMLInputElement).value)"
        />
        <kbd class="absolute right-2 text-[11px] text-neutral50" aria-hidden="true">/</kbd>
      </label>

      <SelectFilter
        label="Unidades"
        :is-open="openFilter === 'unit'"
        :options="unitOptions"
        multiple
        searchable
        dropdown-width="w-72"
        :selected-value="filters.healthUnitIds.map(String)"
        data-testid="activity-requests-filter-unit"
        @toggle="toggleOpen('unit')"
        @select="onToggleId('healthUnitIds', $event)"
      />
      <SelectFilter
        label="Grupo Operacional"
        :is-open="openFilter === 'group'"
        :options="groupOptions"
        multiple
        searchable
        dropdown-width="w-64"
        :selected-value="filters.operationalGroups"
        data-testid="activity-requests-filter-group"
        @toggle="toggleOpen('group')"
        @select="onToggleText('operationalGroups', $event)"
      />
      <SelectFilter
        label="Escala"
        :is-open="openFilter === 'schedule'"
        :options="scheduleOptions"
        multiple
        searchable
        dropdown-width="w-80"
        :selected-value="filters.scheduleIds.map(String)"
        data-testid="activity-requests-filter-schedule"
        @toggle="toggleOpen('schedule')"
        @select="onToggleId('scheduleIds', $event)"
      />
      <SelectFilter
        label="Turno"
        :is-open="openFilter === 'shift'"
        :options="shiftOptions"
        multiple
        searchable
        dropdown-width="w-96"
        :selected-value="filters.shiftIds.map(String)"
        :title="shiftOptionsUnavailable ? SHIFTS_UNAVAILABLE : undefined"
        data-testid="activity-requests-filter-shift"
        @toggle="toggleOpen('shift')"
        @select="onToggleId('shiftIds', $event)"
      />
      <!-- Acréscimo do dono (25/09): "Modalidade de registro" no lugar de "Canal". -->
      <SelectFilter
        label="Modalidade de registro"
        :is-open="openFilter === 'registrationMode'"
        :options="REGISTRATION_MODE_OPTIONS"
        multiple
        dropdown-width="w-80"
        :selected-value="filters.registrationModes"
        data-testid="activity-requests-filter-registration-mode"
        @toggle="toggleOpen('registrationMode')"
        @select="onToggleText('registrationModes', $event)"
      />
      <SelectFilter
        label="Perna"
        :is-open="openFilter === 'leg'"
        :options="REQUESTED_LEG_OPTIONS"
        multiple
        dropdown-width="w-40"
        :selected-value="filters.requestedLeg"
        data-testid="activity-requests-filter-leg"
        @toggle="toggleOpen('leg')"
        @select="onToggleText('requestedLeg', $event)"
      />
      <DateFilter
        title="Data plantão"
        is-range
        :model-value="stationDateRange"
        data-testid="activity-requests-filter-station-date"
        @select="onStationDate"
      />
      <button
        type="button"
        class="h-8 rounded-md border border-lightGray30 px-3 text-sm font-bold text-primary30"
        :aria-expanded="isMoreOpen"
        aria-controls="activity-requests-more-filters"
        data-testid="activity-requests-more-filters-button"
        @click="isMoreOpen = !isMoreOpen"
      >
        {{ moreCount ? `Mais filtros (${moreCount})` : 'Mais filtros' }}
      </button>
    </div>

    <p
      v-if="shiftOptionsUnavailable"
      class="m-0 text-xs text-neutral50"
      data-testid="activity-requests-shifts-unavailable"
    >
      {{ SHIFTS_UNAVAILABLE }}
    </p>

    <div
      v-if="isMoreOpen"
      id="activity-requests-more-filters"
      class="flex flex-wrap items-start gap-6 rounded-lg border border-lightGray30 bg-white p-3"
      data-testid="activity-requests-more-filters-panel"
    >
      <div class="flex flex-col gap-1">
        <span class="text-xs font-bold text-neutral50">Prazo personalizado</span>
        <DateFilter
          title="Prazo (de/até)"
          is-range
          :model-value="deadlineRange"
          data-testid="activity-requests-filter-deadline"
          @select="onDeadline"
        />
      </div>
      <div
        v-for="tri in TRI_STATES"
        :key="tri.key"
        class="flex flex-col gap-1"
        :data-testid="`activity-requests-tri-${tri.key}`"
      >
        <span class="text-xs font-bold text-neutral50">{{ tri.label }}</span>
        <div
          class="inline-flex rounded-full border border-lightGray30 p-0.5"
          role="group"
          :aria-label="tri.label"
        >
          <button
            v-for="option in TRI_OPTIONS"
            :key="option.id"
            type="button"
            class="rounded-full px-3 py-1 text-xs"
            :class="
              filters[tri.key] === option.value
                ? 'bg-primary30 text-white font-bold'
                : 'text-neutral50'
            "
            :aria-pressed="filters[tri.key] === option.value"
            :data-testid="`activity-requests-tri-${tri.key}-${option.id}`"
            @click="setTri(tri.key, option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>

    <ActiveFiltersTags
      :filters="chips"
      :show-clear-button="hasActive"
      test-id-prefix="activity-requests-chips"
      @remove="onRemoveChip"
      @clear-all="emit('clear')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import ActiveFiltersTags from '@/components/Filters/ActiveFilters.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { ACTIVITY_REQUEST_SEARCH_DEBOUNCE_MS } from '@/constants/activity-requests.constants'
import { parseLocalDate, toDateOnly } from '@/utils/date'

import {
  countMoreFilters,
  hasActiveActivityRequestFilters
} from '../../composables/useActivityRequestFilters'
import type { ActivityRequestFilters } from '../../composables/useActivityRequestFilters'
import {
  REGISTRATION_MODE_OPTIONS,
  REQUESTED_LEG_OPTIONS,
  buildActivityRequestChips,
  chipRemovalPatch
} from '../../utils/activity-request-filter-options'
import type { ActivityRequestChip, FilterOption } from '../../utils/activity-request-filter-options'

const props = defineProps<{
  filters: ActivityRequestFilters
  unitOptions: FilterOption[]
  scheduleOptions: FilterOption[]
  shiftOptions: FilterOption[]
  groupOptions: FilterOption[]
  shiftOptionsUnavailable?: boolean
}>()

const emit = defineEmits<{
  update: [patch: Partial<ActivityRequestFilters>]
  clear: []
}>()

const SHIFTS_UNAVAILABLE = 'Turnos indisponíveis para o seu perfil — filtre por escala.'

type IdKey = 'healthUnitIds' | 'scheduleIds' | 'shiftIds'
type TextKey = 'operationalGroups' | 'registrationModes' | 'requestedLeg'
type TriKey = 'hasAntecipe' | 'hasInCash' | 'reopened'

const TRI_STATES: Array<{ key: TriKey; label: string }> = [
  { key: 'hasAntecipe', label: 'Com Antecipe' },
  { key: 'hasInCash', label: 'Com À vista' },
  { key: 'reopened', label: 'Reaberta após rejeição' }
]
const TRI_OPTIONS: Array<{ id: 'any' | 'yes' | 'no'; label: string; value: boolean | null }> = [
  { id: 'any', label: 'Tanto faz', value: null },
  { id: 'yes', label: 'Sim', value: true },
  { id: 'no', label: 'Não', value: false }
]

const openFilter = ref<string | null>(null)
const isMoreOpen = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)

const toggleOpen = (key: string): void => {
  openFilter.value = openFilter.value === key ? null : key
}

const toggleInList = <T,>(list: readonly T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

// Chave computada vira índice `string` no TS: o cast diz que o patch é parcial dos filtros.
function onToggleId(key: IdKey, raw: string): void {
  const next = toggleInList(props.filters[key], Number(raw))
  emit('update', { [key]: next } as Partial<ActivityRequestFilters>)
}

function onToggleText(key: TextKey, raw: string): void {
  const next = toggleInList(props.filters[key] as readonly string[], raw)
  emit('update', { [key]: next } as Partial<ActivityRequestFilters>)
}

function setTri(key: TriKey, value: boolean | null): void {
  emit('update', { [key]: value } as Partial<ActivityRequestFilters>)
}

/* ---------------------------------------------------------------- busca */

const searchText = ref(props.filters.search)
let searchDebounce: ReturnType<typeof setTimeout> | null = null
const cancelSearchDebounce = (): void => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = null
}

function onSearchInput(value: string): void {
  searchText.value = value
  cancelSearchDebounce()
  searchDebounce = setTimeout(() => {
    searchDebounce = null
    emit('update', { search: value })
  }, ACTIVITY_REQUEST_SEARCH_DEBOUNCE_MS)
}

// Mudança de fora (limpar, visão salva, URL): o campo acompanha e a busca pendente cai.
watch(
  () => props.filters.search,
  (search) => {
    if (search === searchText.value) return
    cancelSearchDebounce()
    searchText.value = search
  }
)

/** Atalho `/` (M1, balão 1): foca a busca, a menos que a pessoa já esteja digitando. */
function onGlobalKeydown(event: KeyboardEvent): void {
  if (event.key !== '/') return
  const target = event.target as HTMLElement | null
  const typing =
    target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
  if (typing) return
  event.preventDefault()
  searchInput.value?.focus()
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  cancelSearchDebounce()
})

/* ---------------------------------------------------------------- datas */

const stationDateRange = computed(() =>
  props.filters.startDate || props.filters.endDate
    ? { start: parseLocalDate(props.filters.startDate), end: parseLocalDate(props.filters.endDate) }
    : null
)

function onStationDate(range: { start: Date | null; end: Date | null }): void {
  emit('update', {
    startDate: range.start ? toDateOnly(range.start) : null,
    endDate: range.end ? toDateOnly(range.end) : range.start ? toDateOnly(range.start) : null
  })
}

const deadlineRange = computed(() =>
  props.filters.deadlineFrom || props.filters.deadlineTo
    ? {
        start: props.filters.deadlineFrom ? new Date(props.filters.deadlineFrom) : null,
        end: props.filters.deadlineTo ? new Date(props.filters.deadlineTo) : null
      }
    : null
)

/** Prazo "de/até" em dias do navegador: de 00:00 até 23:59:59.999, como instantes ISO (§4.7). */
function onDeadline(range: { start: Date | null; end: Date | null }): void {
  const start = range.start ? new Date(range.start) : null
  const end = range.end ? new Date(range.end) : start ? new Date(start) : null
  start?.setHours(0, 0, 0, 0)
  end?.setHours(23, 59, 59, 999)
  emit('update', {
    deadlineFrom: start ? start.toISOString() : null,
    deadlineTo: end ? end.toISOString() : null
  })
}

/* ---------------------------------------------------------------- chips */

const moreCount = computed(() => countMoreFilters(props.filters))
const hasActive = computed(() => hasActiveActivityRequestFilters(props.filters))

const chips = computed(() =>
  buildActivityRequestChips(props.filters, {
    units: props.unitOptions,
    schedules: props.scheduleOptions,
    shifts: props.shiftOptions
  })
)

function onRemoveChip(chip: ActivityRequestChip): void {
  emit('update', chipRemovalPatch(props.filters, chip))
}
</script>
